import React, { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Undo2 } from 'lucide-react';
import { Button } from '../Button';
import { ServiceItem, ServiceTransaction } from '../../types';
import {
    ReversalTarget,
    annotateReversibleEntries,
    planReversal,
    protocolGap,
    reversalTraces,
} from '../../services/serviceReversalLogic';

interface Props {
    service: ServiceItem;
    serviceLog: ServiceTransaction[];
    /** Wirft nie — der Text der Rueckmeldung landet im Dialog. */
    onReverse: (target: ReversalTarget, reason: string) => Promise<{ ok: boolean; message: string }>;
    onClose: () => void;
}

/**
 * RUECKNAHME einer Leistungsbuchung — das Gegenstueck zum Verzicht.
 *
 * Hier wird gebucht, was NIE ERBRACHT wurde: eine ausgefallene Begehung, eine
 * Dublette, eine von einem Zaehler erfundene Menge. `quantityUsed` sinkt, und
 * die Leistung verschwindet aus dem Nachweis — weil es sie nicht gab.
 *
 * Der Dialog bietet die beiden Gestalten desselben Fehlers nebeneinander an:
 * einen konkreten Protokolleintrag (Dublette) und die Menge, von der ueberhaupt
 * kein Eintrag erzaehlt (direkt hochgesetzter Stand). Die zweite ist die
 * haeufigere und die unauffaelligere — sie sieht in jeder Liste aus wie
 * erbrachte Leistung.
 */
export const ServiceReversalDialog: React.FC<Props> = ({ service, serviceLog, onReverse, onClose }) => {
    // Mit Zwillings- und Ruecknahme-Kennzeichnung: bei einem mehrfach gebuchten
    // Bericht sehen die Eintraege sonst alle gleich aus, und nach der ersten
    // Ruecknahme waehlt man den verbleibenden, richtigen noch einmal
    // (6110, Bericht #25, 02.09.2026).
    const annotated = useMemo(() => annotateReversibleEntries(service, serviceLog), [service, serviceLog]);
    const entries = useMemo(() => annotated.map((a) => a.entry), [annotated]);
    const traces = useMemo(() => reversalTraces(service, serviceLog), [service, serviceLog]);
    const gap = useMemo(() => protocolGap(service, serviceLog), [service, serviceLog]);

    const [auswahl, setAuswahl] = useState<string>(gap > 0 ? '__gap__' : (entries[0]?.id || ''));
    // Die Menge gilt fuer BEIDE Gestalten: bei der Luecke der Teil davon, beim
    // Eintrag der Teil des Eintrags (Vorbelegung = ganzer Eintrag). Weniger als
    // der ganze Eintrag ist die TEILWEISE Ruecknahme — 4 Fahrten an einem Tag
    // gebucht, 3 davon nie gefahren.
    const [menge, setMenge] = useState<string>(gap > 0 ? String(gap) : String(entries[0]?.quantity ?? ''));
    const [grund, setGrund] = useState('');
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const einheit = service.unit || '';
    const istLuecke = auswahl === '__gap__';
    const gewaehlt = istLuecke ? undefined : entries.find((t) => t.id === auswahl);
    // Eine Nachweiszeile der Pauschale traegt keine Menge: kein Mengenfeld,
    // keine Teilruecknahme — sie geht ganz, der Leistungsstand bleibt.
    const istNachweis = !istLuecke && Boolean(annotated.find((a) => a.entry.id === auswahl)?.nachweis);
    const eintragMenge = gewaehlt?.quantity || 0;
    // Auf drei Stellen gerundet wie die Planung — sonst nimmt die Maske
    // 0,0004 an und die Planung weist es als 0 ab.
    const mengeZahl = Math.round(Number((menge || '').replace(',', '.')) * 1000) / 1000;
    const mengeOk = Number.isFinite(mengeZahl) && mengeZahl > 0;
    const auswahlOk = istLuecke
        ? mengeOk && mengeZahl <= gap
        : Boolean(gewaehlt) && (istNachweis || (mengeOk && mengeZahl <= eintragMenge + 0.0005));
    const grundOk = grund.trim().length >= 3;
    const target: ReversalTarget = istLuecke
        ? { kind: 'gap', quantity: mengeZahl }
        : istNachweis
            ? { kind: 'entry', entryId: auswahl }
            : { kind: 'entry', entryId: auswahl, quantity: mengeZahl };

    // VORSCHAU aus derselben Planung, die spaeter schreibt: was bleibt, um
    // wie viel der Zaehler wirklich sinkt (bei einem Zwilling, den er nie
    // gezaehlt hat, um weniger als die Menge) und welche Schranke greift —
    // BEVOR jemand „Zuruecknehmen" drueckt. Rechnungszuordnungen kennt erst
    // der Schreibpfad; die Meldung dazu kommt nach dem Klick.
    // Ohne useMemo: die Planung ist reine Rechnung ueber ein kurzes Protokoll
    // und billiger als die Abhaengigkeitsliste, die sie brauchte.
    const vorschau = auswahlOk
        ? planReversal(service, serviceLog, {
            target,
            reason: grundOk ? grund.trim() : 'Vorschau',
            actorName: '',
            now: '',
            traceId: '',
            reference: '',
        })
        : null;
    const planOk = Boolean(vorschau?.ok && vorschau.plan);
    const gekuerzt = vorschau?.plan?.reducedEntry;
    const usedDelta = vorschau?.plan?.usedDelta ?? 0;

    const waehle = (id: string, vorbelegung: number) => {
        setAuswahl(id);
        setMenge(String(vorbelegung));
    };

    const absenden = async () => {
        setLaeuft(true);
        setFehler(null);
        const ergebnis = await onReverse(target, grund.trim());
        if (ergebnis.ok) {
            onClose();
            return;
        }
        setFehler(ergebnis.message);
        setLaeuft(false);
    };

    const tag = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('de-DE') : 'ohne Datum');
    // Sekundengenau: Zwillinge liegen 50 bis 550 ms auseinander und tragen
    // denselben Tag — nur die Buchungszeit unterscheidet sie.
    const uhrzeit = (iso?: string) =>
        iso ? new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-xl shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
                <div className="flex items-start gap-4 mb-4">
                    <div className="bg-rose-50 p-3 rounded-full"><Undo2 className="w-6 h-6 text-rose-600" /></div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-lg text-slate-800">Buchung zurücknehmen</h3>
                        <p className="text-sm text-slate-600 mt-1 truncate" title={service.title}>
                            {service.lvPosition ? `${service.lvPosition} — ` : ''}{service.title}
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 mb-4 flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-rose-900 leading-relaxed">
                        <strong>Nur für Leistung, die es nie gab.</strong> Der Leistungsstand sinkt,
                        und die Menge verschwindet aus dem Leistungsnachweis. Hat die Begehung
                        stattgefunden und soll bloß nicht mehr gefordert werden, ist
                        <strong> „Nicht mehr abrechnen"</strong> der richtige Weg — sonst fehlt sie
                        später im Nachweis, obwohl sie erbracht wurde.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-[11px] uppercase text-slate-400 font-bold">Erledigt</div>
                        <div className="font-mono font-bold text-slate-700">{service.quantityUsed || 0} {einheit}</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-[11px] uppercase text-slate-400 font-bold">Abgerechnet</div>
                        <div className="font-mono font-bold text-emerald-600">{service.quantityBilled || 0} {einheit}</div>
                    </div>
                    <div className={`rounded-lg p-3 border ${gap > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="text-[11px] uppercase text-slate-400 font-bold">Ohne Protokoll</div>
                        <div className={`font-mono font-bold ${gap > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{gap} {einheit}</div>
                    </div>
                </div>

                {traces.length > 0 && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4">
                        <div className="text-[11px] uppercase text-slate-500 font-bold mb-1.5">
                            Bereits zurückgenommen ({traces.length})
                        </div>
                        <ul className="space-y-1">
                            {traces.map((t) => (
                                <li key={t.id} className="text-xs text-slate-600">
                                    <span className="font-mono text-slate-400">{tag(t.bookedAt || t.date)} {uhrzeit(t.bookedAt)}</span>
                                    {' · '}
                                    {t.description || t.bookingReference}
                                    {t.bookedBy ? <span className="text-slate-400"> · {t.bookedBy}</span> : null}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <label className="block text-xs font-bold uppercase text-slate-500 mb-2">
                    Was wird zurückgenommen?
                </label>
                <div className="space-y-1.5 mb-4 max-h-64 overflow-y-auto pr-1">
                    {gap > 0 && (
                        <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${istLuecke ? 'border-rose-300 bg-rose-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                            <input
                                type="radio"
                                name="reversal-target"
                                checked={istLuecke}
                                disabled={laeuft}
                                onChange={() => waehle('__gap__', gap)}
                                className="mt-0.5"
                            />
                            <span className="text-sm">
                                <strong>Menge ohne Protokolleintrag</strong> — {gap} {einheit}
                                <span className="block text-[11px] text-slate-500 mt-0.5">
                                    Als erledigt geführt, aber kein Eintrag erzählt davon. So sieht ein
                                    hochgesetzter Zähler aus.
                                </span>
                            </span>
                        </label>
                    )}
                    {annotated.map(({ entry: t, duplicateCount, priorReversals, partiallyReversed, nachweis }) => (
                        <label
                            key={t.id}
                            className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${auswahl === t.id ? 'border-rose-300 bg-rose-50' : 'border-slate-200 hover:bg-slate-50'}`}
                        >
                            <input
                                type="radio"
                                name="reversal-target"
                                checked={auswahl === t.id}
                                disabled={laeuft}
                                onChange={() => waehle(t.id, t.quantity || 0)}
                                className="mt-0.5"
                            />
                            <span className="text-sm min-w-0">
                                <strong>{tag(t.date)}</strong> — {nachweis
                                    ? (
                                        <span
                                            className="inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold uppercase align-middle"
                                            title="Nachweiszeile in der Monatspauschale – ohne Menge"
                                        >
                                            Nachweis
                                        </span>
                                    )
                                    : <>{t.quantity} {einheit}</>}
                                {partiallyReversed > 0 && (
                                    <span className="text-[11px] text-slate-500 ml-1">
                                        (ursprünglich {Math.round(((t.quantity || 0) + partiallyReversed) * 1000) / 1000}, davon {partiallyReversed} zurückgenommen)
                                    </span>
                                )}
                                {t.bookedAt && (
                                    <span className="text-[11px] font-mono text-slate-400 ml-2">gebucht {uhrzeit(t.bookedAt)}</span>
                                )}
                                {duplicateCount > 1 && (
                                    <span className="inline-block ml-2 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-bold uppercase align-middle">
                                        {duplicateCount}× derselbe Bericht
                                    </span>
                                )}
                                <span className="block text-[11px] text-slate-500 mt-0.5">
                                    {t.performerName || 'ohne Namen'}
                                    {t.description ? ` · ${t.description}` : ''}
                                </span>
                                {priorReversals > 0 && duplicateCount === 1 && (
                                    <span className="block text-[11px] text-amber-700 mt-0.5">
                                        Zu diesem Bericht wurde bereits {priorReversals}× zurückgenommen — das ist der verbleibende
                                        Eintrag. Nur zurücknehmen, wenn die Begehung selbst nicht stattfand.
                                    </span>
                                )}
                                {priorReversals > 0 && duplicateCount > 1 && (
                                    <span className="block text-[11px] text-amber-700 mt-0.5">
                                        Zu diesem Bericht wurde bereits {priorReversals}× zurückgenommen, {duplicateCount} Einträge stehen noch.
                                    </span>
                                )}
                            </span>
                        </label>
                    ))}
                    {gap <= 0 && entries.length === 0 && (
                        <p className="text-sm text-slate-500 p-2">
                            Zu dieser Position gibt es nichts zurückzunehmen.
                        </p>
                    )}
                </div>

                {gewaehlt && istNachweis && (
                    <div className="mb-4">
                        {planOk && (
                            <p className="text-[11px] text-slate-500">
                                Nachweiszeile ohne Menge: die Zeile verschwindet aus dem Leistungsnachweis und zählt
                                nicht mehr als Begehung. Der Leistungsstand bleibt bei {service.quantityUsed || 0} {einheit}.
                            </p>
                        )}
                        {vorschau && !vorschau.ok && (
                            <p className="text-[11px] text-amber-700 mt-1">{vorschau.error}</p>
                        )}
                    </div>
                )}

                {(istLuecke || (gewaehlt && !istNachweis)) && (
                    <div className="mb-4">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-bold uppercase text-slate-500">Menge</label>
                            <input
                                type="number"
                                min={0}
                                max={istLuecke ? gap : eintragMenge}
                                step="any"
                                value={menge}
                                disabled={laeuft}
                                onChange={(e) => setMenge(e.target.value)}
                                className="w-28 border border-slate-300 rounded-lg px-3 py-2 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                            />
                            <span className="text-sm text-slate-500">
                                {einheit} von {istLuecke ? gap : eintragMenge} {einheit}
                            </span>
                        </div>
                        {!istLuecke && planOk && (
                            <p className="text-[11px] text-slate-500 mt-1">
                                {gekuerzt
                                    ? <>Teilweise Rücknahme — Rest im Protokolleintrag: <strong>{gekuerzt.to} {einheit}</strong>.</>
                                    : <>Ganzer Eintrag: die Zeile verschwindet aus dem Protokoll. Weniger eintragen, wenn nur ein Teil zurück soll (z. B. 3 von 4 Fahrten).</>}
                                {' '}
                                {usedDelta > 0.0005
                                    ? <>Leistungsstand {service.quantityUsed || 0} → {vorschau!.plan!.service.quantityUsed} {einheit}.</>
                                    : <>Der Leistungsstand bleibt bei {service.quantityUsed || 0} {einheit} (der Zähler hatte diese Menge nie gezählt).</>}
                                {usedDelta > 0.0005 && usedDelta < mengeZahl - 0.0005 && (
                                    <> Der Zähler hatte nur {usedDelta} {einheit} davon gezählt.</>
                                )}
                            </p>
                        )}
                        {auswahlOk && vorschau && !vorschau.ok && (
                            <p className="text-[11px] text-amber-700 mt-1">{vorschau.error}</p>
                        )}
                        {!auswahlOk && (istLuecke || gewaehlt) && (
                            <p className="text-[11px] text-amber-700 mt-1">
                                {!mengeOk
                                    ? <>Bitte eine Menge größer als 0 eingeben.</>
                                    : istLuecke
                                        ? <>Ohne Protokolleintrag stehen nur {gap} {einheit} da.</>
                                        : <>Der Protokolleintrag trägt nur {eintragMenge} {einheit} — mehr lässt sich nicht zurücknehmen.</>}
                            </p>
                        )}
                    </div>
                )}

                {(gap > 0 || entries.length > 0) && (
                    <>
                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                            Begründung (Pflicht)
                        </label>
                        <textarea
                            value={grund}
                            disabled={laeuft}
                            rows={3}
                            onChange={(e) => setGrund(e.target.value)}
                            placeholder="z. B. Begehung ist ausgefallen, der Bericht war eine Dublette vom 09.04."
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                        />
                        <p className="text-[11px] text-slate-400 mt-1 mb-4">
                            Sie steht später im Leistungsprotokoll und in den Projektnotizen.
                        </p>
                    </>
                )}

                {fehler && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4 text-sm text-red-700">{fehler}</div>
                )}

                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={onClose} disabled={laeuft}>Abbrechen</Button>
                    {(gap > 0 || entries.length > 0) && (
                        <Button
                            variant="danger"
                            disabled={laeuft || !auswahlOk || !grundOk || !planOk}
                            onClick={absenden}
                            icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                        >
                            Zurücknehmen
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
